import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { entityNameSelector, taxpayerNumberSelector } from '!core/redux';
import { accountBalanceSelectedSelector } from '!core/redux/accountBalance/accountBalance.selectors';
import { securityAccountSelectedInfoSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { TrlPriceType } from '»components/atoms';
import { useCurrentPage } from '»utils/customHooks';
import { TrlNumberFormatted } from '»utils/number';

import {
  BoxClientStyled,
  BoxIssuanceDividerStyled,
  BoxIssuanceStyled,
  BoxStyled,
  ContainerLeftStyled,
  LabelHeaderStyled,
  LabelStyled,
  LabelValueStyled,
} from './PrimarySimulationCharges.styles';

export const PrimarySimulationCharges = ({ dataItem }) => {
  const {
    offerType,
    trancheDescr,
    priceType,
    nominalValue,
    securityDescription,
    fundManager,
    issuerEntityDescription,
    symbol,
  } = dataItem;

  const { accountDescr, accountHolderTypeDescr } = useSelector(
    securityAccountSelectedInfoSelector,
  );
  const entityName = useSelector(entityNameSelector);
  const taxpayerNumber = useSelector(taxpayerNumberSelector);

  const { isMarketPrimaryPage, isTradingPrimaryPage } = useCurrentPage();

  const titleInfo =
    isMarketPrimaryPage || isTradingPrimaryPage
      ? {
          containerTitle: 'TRLT_UI-ISSUANCE',
          details: [
            {
              label: 'TRLT_UI-DESCRIPTION',
              value: trancheDescr,
              type: 'text',
            },
            {
              label: 'TRLT_UI-EVENT_TYPE',
              value: offerType,
              type: 'text',
            },
            {
              label: 'TRLT_UI-PRICE_TYPE',
              value: priceType,
              type: 'price',
            },
            {
              label: 'TRLT_UI-NOMINAL_VALUE',
              value: nominalValue,
              type: 'number',
            },
          ],
        }
      : {
          containerTitle: 'TRLT_UI-FUND',
          details: [
            {
              label: 'TRLT_UI-SECURITY',
              value: securityDescription,
              type: 'text',
            },
            {
              label: 'TRLT_UI-TICKER',
              value: symbol,
              type: 'text',
            },
            {
              label: 'TRLT_UI-MANAGEMENT_COMPANY',
              value: fundManager,
              type: 'text',
            },
            {
              label: 'TRLT_UI-ISSUER',
              value: issuerEntityDescription,
              type: 'text',
            },
          ],
        };

  const getElement = (value, type) => {
    if (type === 'text') return value;

    if (type === 'price') return <TrlPriceType value={value} />;

    if (type === 'number')
      return <TrlNumberFormatted>{value}</TrlNumberFormatted>;

    return null;
  };

  const cashAccount = useSelector(accountBalanceSelectedSelector);
  return (
    <Box id="divClient">
      <BoxStyled>
        <BoxClientStyled>
          <LabelHeaderStyled>
            <FormattedMessage id="TRLT_UI-CLIENT" />
          </LabelHeaderStyled>
          <ContainerLeftStyled>
            <LabelStyled>
              <FormattedMessage id="TRLT_UI-NAME" />
            </LabelStyled>
            <LabelValueStyled>{entityName}</LabelValueStyled>
            <LabelStyled>
              <FormattedMessage id="TRLT_UI-CUSTODIAN_ACCOUNT" />
            </LabelStyled>
            <LabelValueStyled>{accountDescr}</LabelValueStyled>
            <LabelStyled>
              <FormattedMessage id="TRLT_UI-CASH_ACCOUNT" />
            </LabelStyled>
            <LabelValueStyled>{cashAccount}</LabelValueStyled>
            <LabelStyled>
              <FormattedMessage id="TRLT_UI-SHAREHOLDER" />
            </LabelStyled>
            <LabelValueStyled>{accountHolderTypeDescr}</LabelValueStyled>
            <LabelStyled>
              <FormattedMessage id="TRLT_UI-TAXPAYER" />
            </LabelStyled>
            <LabelValueStyled>{taxpayerNumber}</LabelValueStyled>
          </ContainerLeftStyled>
        </BoxClientStyled>
        <BoxIssuanceDividerStyled />
        <BoxIssuanceStyled>
          <LabelHeaderStyled>
            <FormattedMessage id={titleInfo.containerTitle} />
          </LabelHeaderStyled>
          <ContainerLeftStyled>
            {titleInfo.details.map(({ label, value, type }) => (
              <>
                <LabelStyled>
                  <FormattedMessage id={label} />
                </LabelStyled>
                <LabelValueStyled>{getElement(value, type)}</LabelValueStyled>
              </>
            ))}
          </ContainerLeftStyled>
        </BoxIssuanceStyled>
      </BoxStyled>
    </Box>
  );
};

PrimarySimulationCharges.propTypes = {
  dataItem: PropTypes.object,
};
